<script lang="ts">
import { defineComponent, PropType, ref, computed } from 'vue'
import TimelineUserName from './TimelineUserName.vue'
import backdrop from '@/models/backdrop'
import { useMediaQueryStore } from '@/stores/mediaQuery'

export default defineComponent({
  name: 'TimelineUserNameWrapper',

  components: {
    TimelineUserName,
  },

  props: {
    /**
     * ユーザー名
     */
    name: {
      type: String,
      required: true,
    },

    /**
     * プロフィール画像の URL
     */
    avatarUrl: {
      type: String,
      required: true,
    },

    /**
     * 会社名
     */
    companyName: {
      type: String,
      default: '',
    },

    /**
     * 部署・役職名
     */
    departmentAndPosition: {
      type: String,
      default: '',
    },

    /**
     * （「設定」画面で入力できる）一言コメント
     */
    comment: {
      type: String,
      default: '',
    },

    /**
     * （「設定」画面で入力できる）スキル・経歴
     */
    introduction: {
      type: String,
      default: '',
    },

    /**
     * タイムスタンプ
     */
    date: {
      type: String,
      default: ''
    },

    size: {
      type: String as PropType<'small' | 'normal' | 'large'>,
      default: 'normal',
      validator: (value: string) => {
        return ['small', 'normal', 'large'].includes(value)
      },
    },
  },

  setup(props) {
    // constants
    const BACKDROP_CANCEL_ID_PREFIX = 'timeline-user-name'
    const PROFILE_MARGIN = 16 as const

    // store
    const mediaQueryStore = useMediaQueryStore()

    const rootEl = ref()
    const isProfileHovered = ref(false)
    const isShowPopup = ref(false)
    const profilePosition = ref({
      x: 0,
      y: 0,
      property: '',
    })

    // local variables
    let clientHeight = 0

    const backdropCancelId = computed(() => {
      const uid = new Date().getTime()
      const name = props.name.replace(/\s+/g, '')
      return `${BACKDROP_CANCEL_ID_PREFIX}-${name}-${uid}`
    })

    const renderProfile = computed(() => {
      const additionalInfo = props.date
        ? {
          date: props.date
        }
        : {
          companyName: props.companyName,
          departmentAndPosition: props.departmentAndPosition,
        }

      return {
        name: props.name,
        avatarUrl: props.avatarUrl,
        ...additionalInfo,
      }
    })

    // model の backdrop の処理実体を定義
    backdrop(() => {
      if (mediaQueryStore.context.isUnderPhablet) {
        isShowPopup.value = false
      }
    }, backdropCancelId.value)

    /**
     * コメント者の名前、及びアバター画像を hover した際の詳細プロフィール表示処理
     *
     * @param e マウスイベント
     * @param item hover した対象のタイムライン情報
     */
    const showCommentUserProfile = (e?: MouseEvent) => {
      // hover フラグは必ず立てる（hide メソッド内の wait タイム中に再度 hover された場合に備えるため）
      isProfileHovered.value = true

      // すでに show フラグが立っていたら何もしない（表示状態でプロフィールモーダル上へマウスカーソルを移動させた際のモーダル座標の再更新を防ぐ）
      if (isShowPopup.value) return

      if (e) {
        // 座標取得
        clientHeight = document.documentElement.clientHeight
        profilePosition.value.x = e.clientX

        // マウスの座標が画面下半分の場合は、マウスに対して上側に表示する
        const isBottomHalfOfScreen = (clientHeight / 2 < e.clientY)
        profilePosition.value.y = isBottomHalfOfScreen
          ? clientHeight - e.clientY + PROFILE_MARGIN
          : e.clientY + PROFILE_MARGIN
        profilePosition.value.property = isBottomHalfOfScreen
          ? 'bottom'
          : 'top'
      } else {
        profilePosition.value.x = 0
        profilePosition.value.y = PROFILE_MARGIN
        profilePosition.value.property = 'top'
      }

      isShowPopup.value = true
    }

    const hideCommentUserProfile = () => {
      isProfileHovered.value = false

      // hover フラグをおろしてから一定時間後に hover 状況を見ることで、
      // マウスカーソルのプロフィールモーダル上への移動を可能にする。
      // （プロフィールモーダル上へカーソルが移動していた場合は、showCommentUserProfile の method で再度 hover フラグが立っているので、hide の処理に入らない）
      setTimeout(() => {
        if (!isProfileHovered.value) {
          profilePosition.value.x = 0
          profilePosition.value.y = 0
          profilePosition.value.property = ''
          isShowPopup.value = false
        }
      }, 400)
    }

    const onMouseoverUserInfo = (e?: MouseEvent) => {
      // breakpoint 以下の場合はポップアップを閉じて終了
      if (mediaQueryStore.context.isUnderPhablet) {
        return
      }

      showCommentUserProfile(e)
    }

    const onMouseoutUserInfo = () => {
      // breakpoint 以下の場合はポップアップを閉じて終了
      if (mediaQueryStore.context.isUnderPhablet) {
        return
      }

      hideCommentUserProfile()
    }

    const onClickUserInfo = () => {
      if (!mediaQueryStore.context.isUnderPhablet) return

      if (isShowPopup.value) {
        isShowPopup.value = false
      } else {
        profilePosition.value.x = 0
        profilePosition.value.y = PROFILE_MARGIN
        profilePosition.value.property = 'top'

        isShowPopup.value = true
      }
    }

    return {
      // store
      mediaQueryStore,

      // data
      rootEl,
      isShowPopup,
      isProfileHovered,
      profilePosition,

      // computed
      backdropCancelId,
      renderProfile,

      // methods
      onMouseoverUserInfo,
      onMouseoutUserInfo,
      onClickUserInfo,
    }
  }
})
</script>

<template>
  <div
    ref="rootEl"
    class="l-unit__timelineUserWrapper"
    :data-backdrop-cancel="backdropCancelId">
    <div class="l-unit__timelineUserWrapper--description">
      <TimelineUserName
        :name="renderProfile.name"
        :avatar-url="renderProfile.avatarUrl"
        :company-name="renderProfile.companyName"
        :department-and-position="renderProfile.departmentAndPosition"
        :date="renderProfile.date"
        :size="size"
        @mouseover:item="onMouseoverUserInfo($event)"
        @mouseout:item="onMouseoutUserInfo"
        @click:item="onClickUserInfo" />
    <!-- /.l-unit__name description --></div>

    <div
      :class="[
        'l-unit__timelineUserWrapper--footer',
        { 'is-active': isShowPopup },
      ]"
      :style="{
        [profilePosition.property]: `${profilePosition.y}px`,
        'left': mediaQueryStore.context.isUnderPhablet
          ? '50%'
          : `${profilePosition.x}px`,
      }"
      @mouseover="onMouseoverUserInfo($event)"
      @mouseout="onMouseoutUserInfo"
      @click="onClickUserInfo">
      <TimelineUserName
        :name="name"
        :avatar-url="avatarUrl"
        :company-name="companyName"
        :department-and-position="departmentAndPosition"
        size="large" />

      <div
        v-if="comment"
        class="o-label__comment">
        <p class="o-label__comment--item">
          {{ comment }}
        </p>
      <!-- /.o-label__comment --></div>

      <p
        v-if="introduction"
        class="p u-text__small">
        {{ introduction }}
      </p>
    <!-- /.l-unit__name description --></div>
  <!-- /.l-unit__timelineUserWrapper --></div>
</template>

<style lang="sass" scoped>
.l-unit__timelineUserWrapper
  $root: &
  $detail-width: 480
  position: relative

  & &--footer
    width: changeSizePx2Em($detail-width)
    position: fixed
    padding: 1.25em
    background-color: rgba($bg-white, 1)
    border-radius: 8px
    box-shadow: $box-shadow
    visibility: hidden
    opacity: 0
    z-index: 22 // this value must be higher than the close button in Modal component. (for using mypage comments page)
    +transition($property: opacity, $duration: $transition-long)

    &.is-active
      visibility: visible
      opacity: 1

  +breakpoint
    #{$root}--footer
      width: calc(100% - #{$global-prop-sp * 2}px)
      transform: translateX(-50%)

.o-label__comment
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--item
    font-weight: bold
</style>
